/**
 * Billing and credit statements that must read identically wherever they
 * appear (Terms, FAQ, Pricing, How It Works, Help, top-up, emails). Edit them
 * here and every surface follows, so the copies cannot drift apart.
 *
 * Each statement describes what the billing code actually does. Change the
 * code and the statement together:
 *   - lib/integrations/airgpu/pricing.ts   rates, per-minute metering, storage
 *   - app/api/cron/check-machines/route.ts hourly deductions + zero-balance stop
 *   - app/api/machines/[id]/route.ts       the same deduction when a machine is viewed
 *   - app/api/machines/[id]/stop/route.ts  final deduction on stop
 *   - app/api/machines/[id]/start/route.ts start-up time billed, minimum balance
 */

import { branding } from "@/lib/branding.config";
import { formatCurrency, getCurrencySymbol } from "@/lib/utils/currency";
import {
  INCLUDED_VOLUME_GB,
  MINIMUM_BALANCE_BUFFER_HOURS,
  VOLUME_PRICING_PER_GB,
} from "@/lib/integrations/airgpu/pricing";

export const legalNotices = {
  /**
   * `calculateBillingMinutes` rounds up with a one-minute minimum, and every
   * charge prices those minutes at the hourly rate. Each deduction then sets
   * `lastChargedAt = now` (cron, billing on view), not "start + billed
   * minutes", so the rounded-up remainder is counted again by the next
   * deduction. "Each started minute counts as a full minute" over-promised
   * exactly-once; this sentence is what the code does. (A billing change that
   * carries the remainder forward needs owner sign-off -- report H, Y4.)
   */
  billingModel:
    "Prices are displayed as hourly rates, but usage is metered by the minute: each deduction rounds the time since the previous one up to a whole minute.",

  /**
   * The start route sets `lastStartedAt` when the machine enters "starting",
   * so boot time is billed -- and so is a "starting_pending" (Queued) wait for
   * capacity, once the machine runs. Say so rather than "only while running".
   */
  chargingPeriod:
    "Charges accrue from when a machine is started, including start-up time and any wait for capacity, until it is stopped.",

  /**
   * Deductions happen when 60 minutes have passed since the last one (the
   * cron, or the machine being viewed) and on stop. "Normally" because the
   * cron is external: if it is not configured, the deduction waits for the
   * next view or the stop.
   */
  chargingCadence:
    "Metered usage is deducted from the wallet balance while the machine runs, normally once an hour, and when it is stopped.",

  /**
   * Storage is a term of the hourly rate (`calculateHourlyCost`), so it is
   * only ever charged while the machine runs. The euro sign, not
   * formatCurrency: that would round €0.002 to €0.00.
   */
  storage: `The first ${INCLUDED_VOLUME_GB} GB of disk is included in the hourly rate. Each additional GB adds ${getCurrencySymbol()}${VOLUME_PRICING_PER_GB} per hour to that rate, and disk is not charged while the machine is stopped.`,

  /**
   * A deduction charges `min(cost, balance)` and, when the balance falls
   * short, stops the machine in the same step (cron and billing on view).
   * The balance has a CHECK (balance >= 0). There is no continuous watch:
   * the stop comes at the deduction, which is why the copy must not say
   * "as soon as the balance reaches zero".
   */
  zeroBalance: `The balance is checked each time usage is deducted. If it cannot cover the usage, what is left is deducted, the balance stops at ${formatCurrency(0)} and the machine is stopped automatically. The stop happens at that deduction, not at the moment the balance runs out. A stopped machine keeps its disk and can be started again after more credit is added.`,

  /**
   * Create and start both refuse below `calculateMinimumRequiredBalance`
   * (MINIMUM_BALANCE_BUFFER_HOURS of the machine's rate). Nothing is held.
   */
  startBalance: `Creating or starting a machine requires a balance that covers at least ${MINIMUM_BALANCE_BUFFER_HOURS} hours at that machine's hourly rate. This is a check, not a charge: only metered usage is deducted.`,

  /**
   * Nothing re-polls a machine once it is "running" (the list and machine
   * pages poll only transitional states), so a spot machine the provider
   * reclaims stays "running" in the database and the cron keeps charging it
   * until the customer stops it. Say that at the point of sale: "the provider
   * can stop it" alone reads as "billing ends". The refund route is Terms
   * section 6/8. Shown on the create form, its review step, the machine page
   * and /pricing.
   */
  spotReclaim:
    "The provider can reclaim a spot machine at short notice, which stops it and can lose unsaved work. A reclaim is not detected automatically, so the machine can keep showing as running and keep being charged until you stop it. If a spot machine stops responding, stop it and contact support (also if the stop does not go through): verified time that was charged but not supplied is refunded or credited under the Terms.",

  /** Unspent credit stays refundable under Terms section 8, hence the exception. */
  serviceCredits: `Service credits can only be used for ${branding.name} services. They cannot be transferred, traded or redeemed for cash, except for refunds permitted under the Terms of Service. Credits are not cryptocurrency.`,
} as const;
